import { CheckIcon } from 'lucide-react'

import { Button, Card } from '@/components'
import { Loader } from '@/components/ui'
import { TrialPageFrame } from '@/modules/auth'
import { type UnsubscribeState, useUnsubscribe } from '../useUnsubscribe'

const SUPPORT_EMAIL = 'support@counselpro.ai'

interface UnsubscribePageProps {
  token: string
}

export const UnsubscribePage = ({ token }: UnsubscribePageProps) => {
  const { state, retry } = useUnsubscribe(token)

  return (
    <TrialPageFrame>
      <main className="flex flex-1 items-center justify-center">
        <Card
          aria-busy={state === 'working'}
          className="flex w-full max-w-120 flex-col gap-4 p-6 sm:p-10"
        >
          <UnsubscribeMessage state={state} onRetry={retry} />
        </Card>
      </main>
    </TrialPageFrame>
  )
}

interface UnsubscribeMessageProps {
  state: UnsubscribeState
  onRetry: () => void
}

const UnsubscribeMessage = ({ state, onRetry }: UnsubscribeMessageProps) => {
  if (state === 'unsubscribed') return <Unsubscribed />
  if (state === 'linkNotValid') return <LinkNotValid />
  if (state === 'failed') return <UnsubscribeFailed onRetry={onRetry} />
  return <Unsubscribing />
}

const Unsubscribing = () => (
  <div role="status" className="flex flex-col gap-2">
    <Loader size="md" className="text-brand-deep" />
    <h1 className="text-2xl font-semibold tracking-tight">Unsubscribing</h1>
  </div>
)

const Unsubscribed = () => (
  <div role="status" className="flex flex-col gap-4">
    <span className="flex size-10 items-center justify-center rounded-full bg-brand/10 text-brand-deep">
      <CheckIcon aria-hidden className="size-5" />
    </span>
    <h1 className="text-2xl font-semibold tracking-tight">Unsubscribed</h1>
    <p className="text-[15px] text-muted-foreground">
      You are unsubscribed from CounselPro reminder emails. Emails about your
      account still arrive.
    </p>
  </div>
)

const LinkNotValid = () => (
  <div role="alert" className="flex flex-col gap-4">
    <h1 className="text-2xl font-semibold tracking-tight">
      This unsubscribe link is not valid.
    </h1>
    <p className="text-[15px] text-muted-foreground">
      To stop reminder emails, write to <SupportEmail />.
    </p>
  </div>
)

const UnsubscribeFailed = ({ onRetry }: { onRetry: () => void }) => (
  <div role="alert" className="flex flex-col gap-4">
    <h1 className="text-2xl font-semibold tracking-tight">That did not work</h1>
    <p className="text-[15px] text-muted-foreground">
      We could not unsubscribe you. Try again, or write to <SupportEmail />.
    </p>
    <Button size="lg" className="w-full" onClick={onRetry}>
      Try again
    </Button>
  </div>
)

const SupportEmail = () => (
  <a
    href={`mailto:${SUPPORT_EMAIL}`}
    className="font-medium text-brand-deep hover:underline"
  >
    {SUPPORT_EMAIL}
  </a>
)
